import type { AccountRole } from "../../_types/accounts.types";

export type CreateAccountTab = "identity" | "access" | "security";

export type SecurityMode = "invite" | "manual";

export type CreateAccountPayload = {
  firstName: string;
  lastName: string;
  email: string;
  role: AccountRole;
  sendSetPasswordEmail: boolean;
  password?: string;
  groupIds: string[];
};

export const CREATE_ACCOUNT_TAB_LABELS: Record<CreateAccountTab, string> = {
  identity: "Identity",
  access: "Access",
  security: "Security",
};

export const validateCreateAccountFields = (input: {
  firstName: string;
  lastName: string;
  email: string;
  securityMode: SecurityMode;
  password: string;
}): {
  ok: boolean;
  invalidTab: CreateAccountTab | null;
  errors: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
  };
} => {
  const errors = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  };
  let invalidTab: CreateAccountTab | null = null;

  if (input.firstName.trim().length < 2) {
    errors.firstName = "At least 2 characters";
    invalidTab = "identity";
  }
  if (input.lastName.trim().length < 2) {
    errors.lastName = "At least 2 characters";
    invalidTab ??= "identity";
  }
  const trimmedEmail = input.email.trim().toLowerCase();
  if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
    errors.email = "Enter a valid email";
    invalidTab ??= "identity";
  }

  if (input.securityMode === "manual") {
    if (input.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
      invalidTab ??= "security";
    }
  }

  return {
    ok: !invalidTab,
    invalidTab,
    errors,
  };
};

export const buildCreateAccountPayload = (input: {
  firstName: string;
  lastName: string;
  email: string;
  role: AccountRole;
  securityMode: SecurityMode;
  password: string;
  groupIds: string[];
}): CreateAccountPayload => {
  const base: CreateAccountPayload = {
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    email: input.email.trim().toLowerCase(),
    role: input.role,
    sendSetPasswordEmail: input.securityMode === "invite",
    groupIds: [...new Set(input.groupIds)],
  };
  if (input.securityMode === "manual") {
    base.password = input.password;
  }
  return base;
};

export const formatGroupSeatLabel = (group: {
  seatLimit: number | null;
  seatOccupied: number;
  licenseStatus: string | null;
}): string => {
  if (group.seatLimit == null) {
    return group.licenseStatus
      ? `License ${group.licenseStatus} · no seat cap`
      : "No seat pool";
  }
  return `Seats ${group.seatOccupied}/${group.seatLimit}${
    group.licenseStatus ? ` · ${group.licenseStatus}` : ""
  }`;
};
